import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { ConfirmationService, LazyLoadEvent, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ProgressData } from 'src/app/modules/shared/dtos/progress-data';
import { ResponseDto } from 'src/app/modules/shared/dtos/response.dto';
import { ResponseModel } from 'src/app/modules/shared/models/reponse.model';
import { ProgressIndicatingService } from 'src/app/modules/shared/services/progress-indicating.service';
import { AuditTrailRequestDto } from '../../dtos/request-dto/audit-trail-request.dto';
import { AuditTrailResponseDto } from '../../dtos/response-dto/audit-trail-response-dto';
import { TransactionTrailResponseDto } from '../../dtos/response-dto/transaction-response-dto';
import { AuditTrailService } from '../../services/audit-trail.service';
import { TransactionsService } from '../../services/transactions.service';
import { ColumnNamesUtilityService } from '../../utilities/column-names-utility.service';
import * as FileSaver from 'file-saver';
import * as xlsx from 'xlsx';
import { Selections } from '../../interfaces/selections-data';
import { ConfigurationFilterData } from '../../interfaces/configuration-filter-data';

@Component({
    selector: 'app-audit-trail',
    templateUrl: './audit-trail.component.html',
    styleUrls: ['./audit-trail.component.scss']
})
export class AuditTrailComponent implements OnInit {
  public queryType : Array<object> = [];
  private progress$ = new Subject<ProgressData>();
  public partnerDetails: Array<{}> = [];
  public columnList : Array<object> = [];
  public first = 0;
  public rows = 10;
  public maxScheduledEndDate : Date;
  public maxScheduledStartDate : Date
  public showMessage : string = '';
  public transactionReport : Array<object> = [];
  public configurationReport : Array<object> = [];
  public isDataAvailable: boolean = false;
  public pageNumber: number = 0;
  public totalRecords: number = 0;
  public sortField : string = '';
  public sortOrder : string = '';
  public norecordsFound : boolean = false;
  public transactionStatusList : Array<object> = [];
  public configurationStatusList : Array<object> = [];
  public statusList : Array<object> = [];
  public allEventTypes : Array<object> = [];
  public allEventActions : Array<object> = [];
  public eventTypes : Selections;
  public previousData : ConfigurationFilterData;

  public constructor(public fb: FormBuilder,
                    private _auditTrailService : AuditTrailService,
                    private progressService: ProgressIndicatingService,
                    private messageService: MessageService,
                    private _columnNamesUtilityService : ColumnNamesUtilityService,
                    private _transactionsService: TransactionsService,
                    private confirmationService: ConfirmationService) { }

    public filterDataForm = new FormGroup({
        queryType: new FormControl('', [
            Validators.required
        ]),
        partnerId: new FormControl(''),
        eventType: new FormControl(''),
        action: new FormControl(''),
        startDate: new FormControl('', Validators.required),
        endDate: new FormControl(''),
        status: new FormControl('')
    });

    public ngOnInit(): void {
        this.isDataAvailable = false;
        this.norecordsFound = false;
        this.queryType = [
            { name: 'Configuration Changes', value: 'Configuration Changes' },
            { name: 'Transactions', value: 'Transactions' }
        ];
        this.getPartners();
        this.reportSelection();
        this.transactionStatusList = this._columnNamesUtilityService.getTransactionStatus();
        const fiveMinutesIntoFuture = new Date();
        fiveMinutesIntoFuture.setMinutes(fiveMinutesIntoFuture.getMinutes());
        const fiveMinutesIntoFutureUtc = new Date(fiveMinutesIntoFuture.getTime() + fiveMinutesIntoFuture.getTimezoneOffset() * 60 * 1000);
        this.maxScheduledStartDate = fiveMinutesIntoFutureUtc;
        this.maxScheduledEndDate = fiveMinutesIntoFutureUtc;
        this.filterDataForm.controls.eventType.disable();
        this.filterDataForm.controls.action.disable();
        this.filterDataForm.controls.status.disable();

        this.filterDataForm.get('endDate')?.valueChanges.subscribe((data: Date) => {
            if (data === null) {
                this.showMessage = '';
            }
        });
    }

    public checkfieldValues() : void {
        const previousDataCheck = {
            eventType: this.previousData?.eventType,
            action: this.previousData?.eventAction,
            status: this.previousData?.status,
            endDate: this.previousData?.endDate,
            partnerId: this.previousData?.partnerId
        };

        let currentDataCheck = {
            eventType: this.filterDataForm.value.eventType ? this.filterDataForm.value?.eventType : '',
            action: this.filterDataForm.value.action ? this.filterDataForm.value?.action : '',
            status: this.filterDataForm.value.status ? this.filterDataForm.value?.status : '',
            endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value?.endDate).format('YYYY-MM-DD') : '',
            partnerId: this.filterDataForm.value?.partnerId ? this.filterDataForm.value?.partnerId : ''
        };

        if (JSON.stringify(previousDataCheck) !== JSON.stringify(currentDataCheck)) {
            this.confirmationService.confirm({
                message: 'Data has been changed. Do you want to continue with new request ?',
                header: 'Confirmation',
                icon: 'pi pi-info-circle',
                acceptLabel: 'Yes',
                acceptVisible: true,
                rejectLabel: 'Cancel',
                rejectVisible: true,
                accept: () => {
                    this.submitData();
                },
                reject: () => {
                    this.filterDataForm.patchValue({
                        eventType: this.previousData.eventType ? this.previousData.eventType : null,
                        action: this.previousData.eventAction ? this.previousData.eventAction : null,
                        status: this.previousData.status ? this.previousData.status : null,
                        endDate: this.previousData?.endDate ? new Date(this.previousData?.endDate) : null,
                        partnerId: this.previousData?.partnerId ? this.previousData?.partnerId : null
                    });
                    this.onCancelSendData();
                    currentDataCheck = JSON.parse(JSON.stringify(previousDataCheck));
                }
            });
        } else {
            this.submitData();
        }
    }

    public onLazyLoad(event:LazyLoadEvent) : void {
        this.sortField = '';
        this.sortOrder = '';
        this.pageNumber = event.first! + event.rows!;
        this.pageNumber /= event.rows!;
        this.rows = event.rows!;
        this.sortField = event.sortField!;
        if (this.sortField !== undefined) {
            if (event.sortOrder === 1) {
                this.sortOrder = 'asc';
            } else if (event.sortOrder === -1) {
                this.sortOrder = 'desc';
            }
        } else {
            this.sortOrder = '';
        }
        this.checkfieldValues();
    }

    public getPartners() : void {
        this.progressService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Partner Details',
                details: 'Please wait...',
                done: false
            }
        });
        this.partnerDetails = [];
        this._auditTrailService.getPartners().pipe(
            filter(response => new ResponseModel(response).successful),
            map(res => res.partnerCUModel)).subscribe(data => {
            this.partnerDetails.push({ partnerId: 'ALL', value: 'ALL' });
            data.forEach(x => this.partnerDetails.push({ partnerId: x.partnerId, value: x.partnerId }));
            this.sendProgressDoneSignal();
        },
        (response: unknown): void => {
            this.sendProgressDoneSignal();
            const res = response as { error: ResponseDto };
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: new ResponseModel(res.error).message });
        });
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
        this.pageNumber = 1;
        this.rows = 10;
    }

    public reportSelection() : void {
        this.progressService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Report Selection Details',
                details: 'Please wait...',
                done: false
            }
        });
        this._auditTrailService.getReportSelection().pipe(
            filter(response => new ResponseModel(response).successful),
            map(res => res.selections)
        ).subscribe(data => {
            this.eventTypes = data;
            this.sendProgressDoneSignal();
        },
        (response: unknown): void => {
            this.sendProgressDoneSignal();
            const res = response as { error: ResponseDto };
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: new ResponseModel(res.error).message });
        });
    }

    public onChangeQuery(event : {value : { name : {}}}) : void {
        this.allEventTypes = [];
        this.allEventActions = [];
        this.showMessage = '';
        this.norecordsFound = false;
        this.filterDataForm.patchValue({
            eventType: null,
            action: null,
            status: null,
            partnerId: null,
            // startDate: '',
            endDate: ''
        });
        this.isDataAvailable = false;
        if (event.value.name !== '' || event.value.name !== undefined || event.value.name !== null) {
            this.filterDataForm.controls.eventType.enable();
            this.filterDataForm.controls.action.enable();
            this.filterDataForm.controls.status.enable();
        }
        if (event.value.name === 'Configuration Changes') {
            this.allEventTypes = this.eventTypes.auditEventTypes;
            this.allEventActions = this.eventTypes.auditEventActions;
        } else if (event.value.name === 'Transactions') {
            this.allEventTypes = this.eventTypes.transactionEventTypes;
            this.allEventActions = this.eventTypes.transactionEventActions;
            this.statusList = this.transactionStatusList;
        }
    }

    public postAuditReport(request: AuditTrailRequestDto, isExport: boolean) : void {
        this.previousData = {};
        if (isExport === true) {
            this.progressService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Exporting Data in progress',
                    details: 'Please wait ...',
                    done: false
                }
            });
        } else {
            this.progressService.show({
                progress$: this.progress$.asObservable(),
                initial: {
                    header: 'Loading Audit Report Details',
                    details: 'Please wait ...',
                    done: false
                }
            });
        }

        if (this.filterDataForm.value.queryType.name === 'Configuration Changes') {
            this.norecordsFound = false;
            this._auditTrailService.postConfigurationReport(request).pipe(
                filter((response: AuditTrailResponseDto) => response.responseCode === 0 || response.responseCode !== 0))
                .subscribe((data: AuditTrailResponseDto) => {
                    this.previousData = JSON.parse(JSON.stringify(request.filter));
                    if (data.results?.data !== undefined) {
                        /** Export as Excel starts */
                        if (isExport === true) {
                            import('xlsx').then(xlsx => {
                                const worksheet = xlsx.utils.json_to_sheet((data.results.data));
                                const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                                const excelBuffer: unknown = xlsx.write(workbook, {
                                    bookType: 'xlsx',
                                    type: 'array'
                                });
                                this.saveAsExcelFile(excelBuffer, 'Audit Trail-configuration');
                                this.sendProgressDoneSignal();
                            });
                        } else {
                            this.configurationReport = [];
                            this.sendProgressDoneSignal();
                            this.isDataAvailable = true;
                            data.results.data.forEach(x => {
                                if (x.logEventBy) {
                                    x.logEventBy = x.logEventBy.replace(/@hexagon.com/gi, '');
                                    x.logEventBy = x.logEventBy.replace(/@veripose.com/gi, '');
                                }
                            });
                            this.configurationReport = data.results.data;
                            this.totalRecords = data.results.total;
                            this.columnList = this._columnNamesUtilityService.getConfigurationColumnName();
                        }
                        /** Export as Excel ends */
                    } else {
                        this.configurationReport = [];
                        this.norecordsFound = true;
                        this.sendProgressDoneSignal();
                    }
                },
                error => {
                    this.apiResponsePopup(error.error.responseMessage!, error.responseCode!);
                    this.sendProgressDoneSignal();
                });
        } else if (this.filterDataForm.value.queryType.name === 'Transactions') {
            this.norecordsFound = false;
            this._transactionsService.postTransactionReport(request).pipe(
                filter((response: TransactionTrailResponseDto) => response.responseCode === 0 || response.responseCode !== 0))
                .subscribe((data: TransactionTrailResponseDto) => {
                    this.previousData = JSON.parse(JSON.stringify(request.filter));
                    if (data.results?.data !== undefined) {
                        /** Export as Excel starts */
                        if (isExport === true) {
                            const worksheet = xlsx.utils.json_to_sheet((data.results.data));
                            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                            const excelBuffer: unknown = xlsx.write(workbook, {
                                bookType: 'xlsx',
                                type: 'array'
                            });
                            this.saveAsExcelFile(excelBuffer, 'Audit Trail-transactions');
                            this.sendProgressDoneSignal();
                        } else {
                            this.configurationReport = [];
                            this.sendProgressDoneSignal();
                            this.isDataAvailable = true;
                            this.configurationReport = data.results.data;
                            this.totalRecords = data.results.total;
                            this.columnList = this._columnNamesUtilityService.getTransactionColumnName();
                        }
                        /** Export as Excel starts */
                    } else {
                        this.configurationReport = [];
                        this.norecordsFound = true;
                        this.sendProgressDoneSignal();
                    }
                },
                error => {
                    this.apiResponsePopup(error.error.responseMessage!, error.responseCode!);
                    this.sendProgressDoneSignal();
                });
        }
    }

    public clearSortFields() : void {
        this.sortField = '';
        this.sortOrder = '';
    }

    public displayLog() : void {
        this.clearSortFields();
        this.isDataAvailable = false;

        if (this.filterDataForm.value.partnerId === 'ALL') {
            this.filterDataForm.patchValue({
                partnerId: ''
            });
        }

        const configurationRequestBody : AuditTrailRequestDto = {
            sortField: '',
            sortOrder: '',
            page: this.pageNumber,
            row: this.rows,
            filter: {
                startDate: this.filterDataForm.value.startDate ? moment(this.filterDataForm.value.startDate).format('YYYY-MM-DD') : '',
                endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value.endDate).format('YYYY-MM-DD') : '',
                eventType: this.filterDataForm.value.eventType ? this.filterDataForm.value.eventType : '',
                eventAction: this.filterDataForm.value.action ? this.filterDataForm.value.action : '',
                status: this.filterDataForm.value.status ? this.filterDataForm.value.status : '',
                partnerId: this.filterDataForm.value.partnerId ? this.filterDataForm.value.partnerId : ''

            }
        };
        this.postAuditReport(configurationRequestBody, false);
    }

    public onCancelSendData() : void {
        const configurationRequestBody : AuditTrailRequestDto = {
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            page: this.pageNumber,
            row: this.rows,
            filter: this.previousData!
        };
        this.postAuditReport(configurationRequestBody, false);
    }

    public submitData() : void {
        if (this.filterDataForm.value.partnerId === 'ALL') {
            this.filterDataForm.patchValue({
                partnerId: ''
            });
        }
        const configurationRequestBody : AuditTrailRequestDto = {
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            page: this.pageNumber,
            row: this.rows,
            filter: {
                startDate: this.filterDataForm.value.startDate ? moment(this.filterDataForm.value.startDate).format('YYYY-MM-DD') : '',
                endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value.endDate).format('YYYY-MM-DD') : '',
                eventType: this.filterDataForm.value.eventType ? this.filterDataForm.value.eventType : '',
                eventAction: this.filterDataForm.value.action ? this.filterDataForm.value.action : '',
                status: this.filterDataForm.value.status ? this.filterDataForm.value.status : '',
                partnerId: this.filterDataForm.value.partnerId ? this.filterDataForm.value.partnerId : ''
            }
        };
        this.postAuditReport(configurationRequestBody, false);
    }

    public validateStartDate(event : Date) : void {
        this.isDataAvailable = false;
        const tempDate = JSON.parse(JSON.stringify(event));
        const fiveMinutesIntoFuture = new Date(tempDate);
        fiveMinutesIntoFuture.setMinutes(fiveMinutesIntoFuture.getMinutes());
        fiveMinutesIntoFuture.setFullYear(fiveMinutesIntoFuture.getFullYear() + 3);
        fiveMinutesIntoFuture.setDate(fiveMinutesIntoFuture.getDate() - 1);

        if (fiveMinutesIntoFuture >= new Date()) {
            this.maxScheduledEndDate = new Date();
        } else {
            this.maxScheduledEndDate = fiveMinutesIntoFuture;
        }

        this.showMessage = '';
        if (this.filterDataForm.value.endDate !== '' && this.filterDataForm.value.endDate !== null) {
            if (event > this.filterDataForm.value.endDate) {
                this.showMessage = 'Start Date should not be greater than End Date';
            } else if (event.getDate() === this.filterDataForm.value.endDate.getDate() &&
                        event.getMonth() === this.filterDataForm.value.endDate.getMonth() &&
                        event.getFullYear() === this.filterDataForm.value.endDate.getFullYear()) {
                this.showMessage = 'Start Date should not be equal to End Date';
            }
        }
    }

    public validateEndDate(event : Date) : void {
        this.showMessage = '';
        if (this.filterDataForm.value.startDate !== '') {
            if (event < this.filterDataForm.value.startDate) {
                this.showMessage = 'End Date should not be less than Start date';
            } else if ((event.getDate() === this.filterDataForm.value.startDate?.getDate()) &&
                       (event.getMonth() === this.filterDataForm.value.startDate?.getMonth()) &&
                       (event.getFullYear() === this.filterDataForm.value.startDate?.getFullYear())) {
                this.showMessage = 'End Date should not be equal to Start Date';
            }
        }
    }

    private apiResponsePopup(responseMsg: string, responseCode: number) :void {
        if (responseCode === 0) {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: responseMsg });
        } else {
            this.messageService.add({ severity: 'error', summary: 'Fail', detail: responseMsg });
        }
    }

    public exportExcel() : void {
        const exportRequest : AuditTrailRequestDto = {
            sortField: this.sortField,
            sortOrder: this.sortOrder,
            filter: {
                startDate: moment(this.filterDataForm.value.startDate).format('YYYY-MM-DD'),
                endDate: this.filterDataForm.value.endDate ? moment(this.filterDataForm.value.endDate).format('YYYY-MM-DD') : '',
                eventType: this.filterDataForm.value.eventType,
                eventAction: this.filterDataForm.value.action,
                status: this.filterDataForm.value.status?.value,
                partnerId: this.filterDataForm.value?.partnerId ? this.filterDataForm.value.partnerId : ''
            }
        };
        this.postAuditReport(exportRequest, true);
    }

    public saveAsExcelFile(buffer: any, fileName: string): void {
        const EXCEL_TYPE =
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const EXCEL_EXTENSION = '.xlsx';
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE
        });
        FileSaver.saveAs(
            data,
            fileName + '_' + moment().utc().format('MMMM Do YYYY HH:mm:ss') + EXCEL_EXTENSION
        );
    }
}
